import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AssignedRatingSelection, GroupWithMembers, MedicalCondition, MemberWithRatings, RatingWithEffectiveDebits } from "../../types";
import { useShortcuts } from "../../hooks/useShortcuts";
import { useGroupStore } from "../../store/groupStore";
import { notify } from "../../store/uiStore";
import { PageHeader } from "../PageHeader";
import { Card } from "../Card";
import { Button } from "../Button";
import { TagPill } from "../Pills";
import { EmptyState } from "../ScreenStates";
import { RatingsTable } from "./RatingsTable";
import { EMPTY_SELECTION_DRAFT, type RatingSelectionDraft } from "./ratingSelection";
import { SelectedRatingsPanel } from "./SelectedRatingsPanel";
import { GuidancePanel } from "./GuidancePanel";
import { AuroraModal } from "./AuroraModal";
import { RatingChangeModal, type RatingChangeDetails } from "../member/RatingChangeModal";
import { fullName } from "../../utils/format";

type PendingStep = "none" | "aurora" | "changeReason";

interface RatingSelectionWorkspaceProps {
  group: GroupWithMembers;
  member: MemberWithRatings;
  condition: MedicalCondition;
  ratings: readonly RatingWithEffectiveDebits[];
  children?: never;
}

function summarize(created: number, updated: number, autoAdded: readonly string[], substituted: readonly string[]): string {
  const parts: string[] = [];
  if (created > 0) parts.push(`Added ${created} rating${created === 1 ? "" : "s"}.`);
  if (updated > 0) parts.push(`Updated ${updated} existing rating${updated === 1 ? "" : "s"}.`);
  if (autoAdded.length > 0) parts.push(`Also added: ${autoAdded.join(", ")}.`);
  if (substituted.length > 0) parts.push(`${substituted.join("; ")}.`);
  return parts.join(" ") || "No changes.";
}

export function RatingSelectionWorkspace({ group, member, condition, ratings }: RatingSelectionWorkspaceProps) {
  const navigate = useNavigate();
  const addAssignedRatings = useGroupStore((state) => state.addAssignedRatings);
  const [selections, setSelections] = useState(() => new Map<number, RatingSelectionDraft>());
  // Ratings the member already holds are shown but cannot be picked again; they are edited or removed on the member screen (Brady, 2026-09-07).
  const assignedRatingIds = useMemo(() => new Set(member.assignedRatings.flatMap((assigned) => (assigned.ratingId === null ? [] : [assigned.ratingId]))), [member.assignedRatings]);
  const [pendingStep, setPendingStep] = useState<PendingStep>("none");
  const [isAdding, setIsAdding] = useState(false);
  const memberName = fullName(member.firstName, member.lastName);
  const memberOverviewPath = `/uw/groups/${group.id}/members/${member.id}`;
  const entries = useMemo(() => ratings.filter((rating) => selections.has(rating.ratingId)).map((rating) => ({ rating, draft: selections.get(rating.ratingId) ?? EMPTY_SELECTION_DRAFT })), [ratings, selections]);
  const auroraRatings = useMemo(() => (group.hasSrxCarveOut ? entries.map((entry) => entry.rating).filter((rating) => rating.specialtyDrug !== null) : []), [group.hasSrxCarveOut, entries]);
  const requiresChangeReason = group.isAiV2Copy && member.wasOnSkynetOriginal;

  // Stable handlers: RatingsTable memoizes its rows on them, so a click re-renders one row out of hundreds.
  const handleToggle = useCallback((rating: RatingWithEffectiveDebits, checked: boolean) => {
    setSelections((current) => {
      const next = new Map(current);
      if (checked) next.set(rating.ratingId, EMPTY_SELECTION_DRAFT);
      else next.delete(rating.ratingId);
      return next;
    });
  }, []);

  const handleRemove = useCallback(
    (ratingId: number) =>
      setSelections((current) => {
        const next = new Map(current);
        next.delete(ratingId);
        return next;
      }),
    [],
  );

  const handleDraftChange = useCallback((ratingId: number, draft: RatingSelectionDraft) => setSelections((current) => new Map(current).set(ratingId, draft)), []);

  const finishAdd = useCallback(
    async (details: RatingChangeDetails | null) => {
      // Feedback 5.8: the reason "IRIX Sourced" flags every rating in the add as IRIX Source.
      const irixImplied = details?.changeReason === "IRIX Sourced";
      const payload: AssignedRatingSelection[] = [...selections.entries()].map(([ratingId, draft]) => ({ ratingId, debitsOverride: draft.debitsOverride, uwDollars: draft.uwDollars, irixSource: draft.irixSource || irixImplied, notes: draft.notes.trim() || details?.notes || null }));
      setIsAdding(true);
      const result = await addAssignedRatings(member.id, { selections: payload, changeReason: details?.changeReason ?? null, changeExplanation: details?.changeExplanation ?? null });
      setIsAdding(false);
      setPendingStep("none");
      if (!result) return;
      const autoAdded = result.autoAdded.map((rating) => `${rating.ratingText} (${rating.assignedDebits})`);
      const substituted = result.substitutions.map((entry) => `${entry.brandName.replace("Rx : ", "")} loaded from generic ${entry.genericName.replace("Rx : ", "")}`);
      notify(summarize(result.created.length, result.updated.length, autoAdded, substituted), "success");
      navigate(memberOverviewPath);
    },
    [selections, addAssignedRatings, member.id, navigate, memberOverviewPath],
  );

  // Feedback 5.8: every rating in the add flagged IRIX Source is IRIX sourced, so the reason is known and the prompt is skipped.
  const allIrixSourced = selections.size > 0 && [...selections.values()].every((draft) => draft.irixSource);
  const afterAurora = useCallback(() => {
    if (!requiresChangeReason) void finishAdd(null);
    else if (allIrixSourced) void finishAdd({ notes: null, changeReason: "IRIX Sourced", changeExplanation: null });
    else setPendingStep("changeReason");
  }, [requiresChangeReason, allIrixSourced, finishAdd]);

  const handleAdd = useCallback(() => {
    if (selections.size === 0) return;
    if (auroraRatings.length > 0) setPendingStep("aurora");
    else afterAurora();
  }, [selections.size, auroraRatings.length, afterAurora]);

  const handleCancel = useCallback(() => navigate(memberOverviewPath), [navigate, memberOverviewPath]);

  const shortcuts = useMemo(() => ({ save: handleAdd, cancel: handleCancel }), [handleAdd, handleCancel]);
  useShortcuts(shortcuts);

  return (
    <>
      <PageHeader
        title={condition.name}
        badges={
          <>
            <TagPill label={condition.category} />
            {condition.isCustom && <TagPill label="Custom" tone="green" />}
          </>
        }
        subtitle={`Choose ratings for ${memberName}. Check a row to enable its override, UW Dollars, IRIX Source, and notes.`}
        actions={
          <Button variant="secondary" onClick={handleCancel} disabled={isAdding}>
            Cancel
          </Button>
        }
      />
      <div className="space-y-4">
        <GuidancePanel conditionId={condition.id} />
        <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(280px,1fr)]">
          <Card title="Ratings" actions={<span className="text-xs text-gray-500">{ratings.length} in guideline order</span>}>
            {ratings.length === 0 && <EmptyState message="This condition has no active ratings. Actuarial can add one under Customize Conditions." />}
            {ratings.length > 0 && <RatingsTable ratings={ratings} selections={selections} assignedRatingIds={assignedRatingIds} onToggle={handleToggle} onDraftChange={handleDraftChange} disabled={isAdding} />}
          </Card>
          <SelectedRatingsPanel entries={entries} isAdding={isAdding} onRemove={handleRemove} onAdd={handleAdd} />
        </div>
      </div>
      {pendingStep === "aurora" && <AuroraModal ratings={auroraRatings} onContinue={afterAurora} onCancel={() => setPendingStep("none")} />}
      {pendingStep === "changeReason" && <RatingChangeModal title="Record change" description={`Adding ratings to ${memberName}, who was on the Skynet original.`} requireNote={false} requireChangeReason confirmLabel="Add to Member" onConfirm={finishAdd} onCancel={() => setPendingStep("none")} />}
    </>
  );
}
